import { normalizeCanonicalNutritionEntryDraft } from '@calorie-tracker/nutrition';

import { defaultNutritionModel } from './models.ts';

import type {
  CanonicalNutritionEntryDraft,
  ConfidenceLevel,
  MealType,
} from '@calorie-tracker/nutrition';

export const nutritionWebSearchSystemPrompt = [
  'You resolve unresolved food log drafts using web search.',
  'Search only when the food appears branded, restaurant-specific, packaged, or too ambiguous for a generic nutrition database.',
  'Prefer official brand, restaurant, retailer, manufacturer, or readable nutrition facts sources.',
  'Return source web-search only when cited web evidence directly supports the nutrition values.',
  'Do not invent nutrition facts. If evidence is weak, copy the original draft and set resolved to false.',
  'Scale nutrition values to the requested input quantity only when the serving size or package quantity is clear.',
  'Use high confidence only for direct source matches, medium for clear serving-size scaling, and low for partial or ambiguous matches.',
].join(' ');

export interface OpenAiWebSearchSource {
  title: string;
  url: string;
}

export interface NutritionWebSearchRuntime {
  fetch?: typeof globalThis.fetch;
  nutritionModel?: string;
  openAiApiKey?: string;
}

export interface NutritionWebSearchRawResponse {
  id?: string;
  model?: string;
  outputText: string | null;
  sources: OpenAiWebSearchSource[];
  status?: string;
  usage?: unknown;
}

export interface NutritionWebSearchEnrichmentResult {
  draftEntries: CanonicalNutritionEntryDraft[];
  rawResponse: NutritionWebSearchRawResponse | null;
}

interface ResponsesApiContentPart {
  annotations?: unknown[];
  text?: string;
  type?: string;
}

interface ResponsesApiOutputItem {
  action?: {
    sources?: unknown[];
  };
  content?: ResponsesApiContentPart[];
  type?: string;
}

interface ResponsesApiBody {
  id?: string;
  model?: string;
  output?: ResponsesApiOutputItem[];
  output_text?: string;
  status?: string;
  usage?: unknown;
}

interface WebSearchGeneratedQuantity {
  amount: number;
  unit: string;
}

interface WebSearchGeneratedMacros {
  calories: number;
  proteinGrams: number;
  carbsGrams: number;
  fatGrams: number;
  fiberGrams: number | null;
  sugarGrams: number | null;
  sodiumMilligrams: number | null;
}

interface WebSearchGeneratedEntry {
  canonicalQuantity: WebSearchGeneratedQuantity;
  confidence: ConfidenceLevel;
  consumedAt: string;
  draftIndex: number;
  inputQuantity: WebSearchGeneratedQuantity;
  macros: WebSearchGeneratedMacros;
  mealType: MealType;
  normalizedName: string;
  originalInput: string;
  resolved: boolean;
  sources: OpenAiWebSearchSource[];
}

interface WebSearchGeneratedOutput {
  entries: WebSearchGeneratedEntry[];
}

const nutritionWebSearchResponseSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['entries'],
  properties: {
    entries: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        required: [
          'draftIndex',
          'resolved',
          'mealType',
          'originalInput',
          'normalizedName',
          'inputQuantity',
          'canonicalQuantity',
          'macros',
          'confidence',
          'consumedAt',
          'sources',
        ],
        properties: {
          draftIndex: {
            type: 'integer',
          },
          resolved: {
            type: 'boolean',
          },
          mealType: {
            type: 'string',
            enum: ['breakfast', 'lunch', 'dinner', 'snack'],
          },
          originalInput: {
            type: 'string',
          },
          normalizedName: {
            type: 'string',
          },
          inputQuantity: {
            type: 'object',
            additionalProperties: false,
            required: ['amount', 'unit'],
            properties: {
              amount: {
                type: 'number',
              },
              unit: {
                type: 'string',
              },
            },
          },
          canonicalQuantity: {
            type: 'object',
            additionalProperties: false,
            required: ['amount', 'unit'],
            properties: {
              amount: {
                type: 'number',
              },
              unit: {
                type: 'string',
              },
            },
          },
          macros: {
            type: 'object',
            additionalProperties: false,
            required: [
              'calories',
              'proteinGrams',
              'carbsGrams',
              'fatGrams',
              'fiberGrams',
              'sugarGrams',
              'sodiumMilligrams',
            ],
            properties: {
              calories: {
                type: 'number',
              },
              proteinGrams: {
                type: 'number',
              },
              carbsGrams: {
                type: 'number',
              },
              fatGrams: {
                type: 'number',
              },
              fiberGrams: {
                type: ['number', 'null'],
              },
              sugarGrams: {
                type: ['number', 'null'],
              },
              sodiumMilligrams: {
                type: ['number', 'null'],
              },
            },
          },
          confidence: {
            type: 'string',
            enum: ['high', 'medium', 'low'],
          },
          consumedAt: {
            type: 'string',
          },
          sources: {
            type: 'array',
            items: {
              type: 'object',
              additionalProperties: false,
              required: ['title', 'url'],
              properties: {
                title: {
                  type: 'string',
                },
                url: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
  },
} as const;

function getFetchImplementation(
  runtime?: NutritionWebSearchRuntime,
): typeof globalThis.fetch | null {
  if (runtime?.fetch) {
    return runtime.fetch;
  }

  if (typeof globalThis.fetch === 'function') {
    return globalThis.fetch.bind(globalThis);
  }

  return null;
}

function buildWebSearchInput(draftEntries: CanonicalNutritionEntryDraft[]): string {
  return [
    'Resolve these unresolved food log drafts with current web nutrition sources.',
    'Return one entry for each provided draft. If no reliable source is found, set resolved false and copy the draft values.',
    JSON.stringify(
      draftEntries.map((draft, index) => ({
        draftIndex: index,
        mealType: draft.mealType,
        originalInput: draft.originalInput,
        normalizedName: draft.normalizedName,
        inputQuantity: draft.inputQuantity,
        canonicalQuantity: draft.canonicalQuantity,
        macros: draft.macros,
        confidence: draft.confidence,
        consumedAt: draft.consumedAt,
      })),
    ),
  ].join('\n\n');
}

function readOutputText(response: ResponsesApiBody): string | null {
  if (typeof response.output_text === 'string' && response.output_text.trim().length > 0) {
    return response.output_text;
  }

  for (const outputItem of response.output ?? []) {
    if (outputItem.type !== 'message') {
      continue;
    }

    for (const contentPart of outputItem.content ?? []) {
      if (
        contentPart.type === 'output_text' &&
        typeof contentPart.text === 'string' &&
        contentPart.text.trim().length > 0
      ) {
        return contentPart.text;
      }
    }
  }

  return null;
}

function isSourceLike(value: unknown): value is { title?: unknown; url?: unknown } {
  return typeof value === 'object' && value !== null && 'url' in value;
}

function normalizeSource(value: { title?: unknown; url?: unknown }): OpenAiWebSearchSource | null {
  if (typeof value.url !== 'string' || value.url.trim().length === 0) {
    return null;
  }

  return {
    title: typeof value.title === 'string' ? value.title : '',
    url: value.url,
  };
}

function collectSources(response: ResponsesApiBody): OpenAiWebSearchSource[] {
  const sources = new Map<string, OpenAiWebSearchSource>();

  function addSource(value: unknown) {
    if (!isSourceLike(value)) {
      return;
    }

    const source = normalizeSource(value);

    if (source) {
      sources.set(source.url, source);
    }
  }

  for (const outputItem of response.output ?? []) {
    for (const source of outputItem.action?.sources ?? []) {
      addSource(source);
    }

    for (const contentPart of outputItem.content ?? []) {
      for (const annotation of contentPart.annotations ?? []) {
        addSource(annotation);
      }
    }
  }

  return [...sources.values()];
}

function parseGeneratedOutput(outputText: string): WebSearchGeneratedOutput | null {
  try {
    const parsed = JSON.parse(outputText) as Partial<WebSearchGeneratedOutput>;

    return Array.isArray(parsed.entries) ? (parsed as WebSearchGeneratedOutput) : null;
  } catch {
    return null;
  }
}

function toCanonicalDraft(entry: WebSearchGeneratedEntry): CanonicalNutritionEntryDraft {
  return normalizeCanonicalNutritionEntryDraft({
    mealType: entry.mealType,
    originalInput: entry.originalInput,
    normalizedName: entry.normalizedName,
    inputQuantity: entry.inputQuantity,
    canonicalQuantity: entry.canonicalQuantity,
    macros: {
      calories: entry.macros.calories,
      proteinGrams: entry.macros.proteinGrams,
      carbsGrams: entry.macros.carbsGrams,
      fatGrams: entry.macros.fatGrams,
      ...(entry.macros.fiberGrams != null ? { fiberGrams: entry.macros.fiberGrams } : {}),
      ...(entry.macros.sugarGrams != null ? { sugarGrams: entry.macros.sugarGrams } : {}),
      ...(entry.macros.sodiumMilligrams != null
        ? { sodiumMilligrams: entry.macros.sodiumMilligrams }
        : {}),
    },
    source: 'web-search',
    confidence: entry.confidence,
    consumedAt: entry.consumedAt,
  });
}

export async function enrichNutritionDraftsWithOpenAiWebSearch(input: {
  draftEntries: CanonicalNutritionEntryDraft[];
  runtime?: NutritionWebSearchRuntime;
}): Promise<NutritionWebSearchEnrichmentResult> {
  const apiKey = input.runtime?.openAiApiKey?.trim();
  const fetchImplementation = getFetchImplementation(input.runtime);
  const unresolvedDraftEntries = input.draftEntries.filter(
    (draft) => draft.source === 'ai-estimate',
  );

  if (!apiKey || !fetchImplementation || unresolvedDraftEntries.length === 0) {
    return {
      draftEntries: input.draftEntries,
      rawResponse: null,
    };
  }

  try {
    const model = input.runtime?.nutritionModel?.trim() || defaultNutritionModel;
    const response = await fetchImplementation('https://api.openai.com/v1/responses', {
      method: 'POST',
      headers: {
        authorization: `Bearer ${apiKey}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        model,
        instructions: nutritionWebSearchSystemPrompt,
        input: buildWebSearchInput(unresolvedDraftEntries),
        include: ['web_search_call.action.sources'],
        tools: [
          {
            type: 'web_search',
            search_context_size: 'low',
          },
        ],
        tool_choice: 'required',
        text: {
          format: {
            type: 'json_schema',
            name: 'nutrition_web_search_enrichment',
            description: 'Nutrition facts resolved from web search for unresolved food log drafts.',
            strict: true,
            schema: nutritionWebSearchResponseSchema,
          },
        },
      }),
    });

    if (!response.ok) {
      return {
        draftEntries: input.draftEntries,
        rawResponse: null,
      };
    }

    const responseBody = (await response.json()) as ResponsesApiBody;
    const outputText = readOutputText(responseBody);
    const sources = collectSources(responseBody);
    const generatedOutput = outputText ? parseGeneratedOutput(outputText) : null;

    if (!generatedOutput) {
      return {
        draftEntries: input.draftEntries,
        rawResponse: {
          ...(responseBody.id ? { id: responseBody.id } : {}),
          ...(responseBody.model ? { model: responseBody.model } : {}),
          outputText,
          sources,
          ...(responseBody.status ? { status: responseBody.status } : {}),
          ...(responseBody.usage !== undefined ? { usage: responseBody.usage } : {}),
        },
      };
    }

    const enrichedDraftEntries = [...input.draftEntries];
    const responseSourceUrls = new Set(sources.map((source) => source.url));

    for (const entry of generatedOutput.entries) {
      const originalDraft = unresolvedDraftEntries[entry.draftIndex];
      const hasCitedSource = entry.sources.some((source) => responseSourceUrls.has(source.url));

      if (!originalDraft || !entry.resolved || !hasCitedSource) {
        continue;
      }

      const globalIndex = input.draftEntries.indexOf(originalDraft);

      if (globalIndex >= 0) {
        enrichedDraftEntries[globalIndex] = toCanonicalDraft(entry);
      }
    }

    return {
      draftEntries: enrichedDraftEntries,
      rawResponse: {
        ...(responseBody.id ? { id: responseBody.id } : {}),
        ...(responseBody.model ? { model: responseBody.model } : {}),
        outputText,
        sources,
        ...(responseBody.status ? { status: responseBody.status } : {}),
        ...(responseBody.usage !== undefined ? { usage: responseBody.usage } : {}),
      },
    };
  } catch {
    return {
      draftEntries: input.draftEntries,
      rawResponse: null,
    };
  }
}
